import React, { useContext, useEffect, useRef, useState } from "react"
import { IBreadCrumb } from "../types/common"
import { IPreferences, IRecord } from "../types/schema"
import { IAccessibleReports } from "../Pages/CRM/defaultAccessibleReports"
import { IRELAND_BRANCH_ID } from "../Pages/Reports/const"
import { currencyNameToSymbol } from "../utils/utils"
import { clearBranchGuestSession } from "../utils/branchGuestSession"
import { IRoleSetting, roleSettings } from "../../shared/roleSettings"
import {
  clearExpiredSessionRedirectState,
  clearStoredSession,
  EOS_SKEEM_TOKEN,
  isExpiredSessionError,
} from "../utils/expiredSession"

export function useGlobalState() {
  const value = useContext(GlobalStateContext)

  if (!value) {
    throw new Error("must use global state provider")
  }

  return value
}

export function useEnsureGlobalState() {
  const globalState = useGlobalState()
  if (!globalState.currentUser.id) {
    throw new Error(
      "Current user is not set in global state; Should only use this hook in places where the user must be signed in",
    )
  }
  return globalState as IEnsureGlobalState
}
const emptyUser = {
  id: null,
  isAdmin: false,
  isSuperAdmin: false,
  canExportPurchaseOrdersToSage: false,
  canDespatchOrdersInSage: false,
  canEditGlobalUserPermissions: false,
  canManageProducts: false,
  canManageConsignmentProducts: false,
  isAccounts: false,
  isHrAdmin: false,
  hrManagedUsers: [] as { id: string }[],
  managedByHrUsers: [] as { id: string }[],
  hrAreaOnly: false,
  name: "",
  email: "",
  branch: {
    id: null,
    name: "",
    currency_name: "",
    consignmentWarehouse: undefined,
    euro_pound_exchange_rate: 1,
  },
  userAccountType: "",
  customer: undefined,
  sub_customers: [],
  accessibleBranches: [],
  hrAccessibleBranches: [],
  canRunSageImports: false,
  canViewCRM: false,
  canViewBranchReports: false,
  isBlocked: false,
  canAccessSystemSettings: false,
  canEditBranchSettings: false,
  canUseNonPriceBandPricesOnQuotesOrders: false,
  canSetPricesBelowMinimumMargins: false,
  canEditCustomersPricing: false,
  canDispatchCashSalesOrderWithNoCreditLimit: false,
  canViewGlobalMasterPriceImports: false,
  microsoft_oauth: {},
  beta_features: {},
  accessible_reports: {} as Partial<IAccessibleReports>,
  defaultWarehouse: null,
  canManageBetaFeatureAccess: false,
  canViewPreviewFeatures: false,
  canUpdatePurchaseOrdersInSage: false,
  canManageSupportTicketSettings: false,
  canCreateSageProducts: false,
  canEditExistingProducts: false,
  dashboard_type: "",
  salesRepId: null,
  canManageStockTakes: false,
  canManageSalesRepTargets: false,
  canManageBranchTargets: false,
  hasExistingTradeAccount: false as boolean,
  first_name: "",
  last_name: "",
  companyName: "",
  canDispatchOverCreditLimitOrders: false,
  canGrantReportPermissions: false,
  canManageSalesRepAssignments: false,
  canDeletePriceBands: false,
  canCreateStockTransfers: false,
  canManageUniversalPricing: false,
  canCopyPriceBandsInterBranch: false,
  canSendBulkEmails: false,
  customerIsPrimaryAccountHolder: false,
  customerCanViewFinances: false,
  canViewFortusLiveAccounts: false,
  canGuestLoginToCustomerAccounts: false,
  canManageCustomerTags: false,
  canManageHolidayRequests: false,
  reports_viewable_branches: [] as { id: string }[],
  accessibleBulkEmailBranches: [] as { id: string }[],
  canViewOtherUsersEvents: false,
  canArchiveQuotes: false,
  canArchiveOrders: false,
  canExportCustomers: false,
  canManageCRMPermissions: false,
  canManageHRPermissions: false,
  enablePhoneMFA: false,
  canViewCustomerStatements: false,
  customer_view_only: false,
  canMigrateSalesRepInvoiceItems: false,
  canViewCustomerLists: false,
  canEditOrderRequests: false,
  canShuntOrderRequests: false,
  canExportNewProducts: false,
  canAssignCustomerTags: false,
  canDeleteUnexportedProducts: false,
  canManageProductMasterTags: false,
  canAssignProductMasterTags: false,
  canManageSalesRestrictions: false,
  canViewInternalTickets: false,
  can_approve_manual_invoice_items: false,
  can_create_manual_invoice_items: false,
  canBypassSpendLimitOnNewBusiness: false,
  preferences: {} as IPreferences,
  country: "all",
  customerAreaQuotesOnly: false,
  canManageBuyingGroups: false,
  canApproveByPassMinMargins: false,
  canApproveElevatedMinPriceBypass: false,
  canManageNamedPriceBands: false,
  can_set_site_banner_message: false,
  microsoft_user_details: null as null | Record<string, any>,
  defaultSupportTicketGroup: null as IRecord<
    "support_ticket_groups",
    "name" | "isTechnical"
  > | null,
  defaultCustomerTagFilter: null as
    | (IRecord<"tag_values", "value"> & {
        tag?: IRecord<"tags", "key">
      })
    | null,
  canManageRoles: false as boolean,
  can_manage_sage_auth: false as boolean,
  can_manage_customer_slide_show: false as boolean,
  canDeleteUniversalPriceBands: false as boolean,
  canUpdateManagingUsersOnPriceBands: false as boolean,
  canEditLinkTooltips: false as boolean,
  access_url: "" as string,
}
export type ICurrentUser = IRecord<
  "skeem_users",
  | "can_manage_sage_auth"
  | "can_manage_customer_slide_show"
  | "name"
  | "email"
  | "isAdmin"
  | "isSuperAdmin"
  | "isAccounts"
  | "canExportPurchaseOrdersToSage"
  | "canDespatchOrdersInSage"
  | "canEditGlobalUserPermissions"
  | "userAccountType"
  | "customer"
  | "last_seen"
  | "canManageProducts"
  | "canManageConsignmentProducts"
  | "canRunSageImports"
  | "canViewCRM"
  | "canViewBranchReports"
  | "isBlocked"
  | "beta_features"
  | "accessible_reports"
  | "canAccessSystemSettings"
  | "canEditBranchSettings"
  | "canUseNonPriceBandPricesOnQuotesOrders"
  | "canSetPricesBelowMinimumMargins"
  | "canEditCustomersPricing"
  | "canDispatchCashSalesOrderWithNoCreditLimit"
  | "canViewGlobalMasterPriceImports"
  | "canManageBetaFeatureAccess"
  | "canViewPreviewFeatures"
  | "canUpdatePurchaseOrdersInSage"
  | "canManageSupportTicketSettings"
  | "canCreateSageProducts"
  | "canEditExistingProducts"
  | "dashboard_type"
  | "canManageStockTakes"
  | "canManageSalesRepTargets"
  | "canManageBranchTargets"
  | "hasExistingTradeAccount"
  | "companyName"
  | "first_name"
  | "last_name"
  | "canDispatchOverCreditLimitOrders"
  | "canGrantReportPermissions"
  | "canManageSalesRepAssignments"
  | "canDeletePriceBands"
  | "canCreateStockTransfers"
  | "reports_viewable_branches"
  | "accessibleBulkEmailBranches"
  | "canManageUniversalPricing"
  | "canUpdateManagingUsersOnSharePriceBands"
  | "canUpdateManagingUsersOnPriceBands"
  | "canEditLinkTooltips"
  | "canCopyPriceBandsInterBranch"
  | "canSendBulkEmails"
  | "canViewOtherUsersEvents"
  | "customerIsPrimaryAccountHolder"
  | "customerCanViewFinances"
  | "canViewFortusLiveAccounts"
  | "canGuestLoginToCustomerAccounts"
  | "canManageCustomerTags"
  | "canManageHolidayRequests"
  | "canArchiveQuotes"
  | "canArchiveOrders"
  | "canExportCustomers"
  | "canManageCRMPermissions"
  | "canManageHRPermissions"
  | "enablePhoneMFA"
  | "canViewCustomerStatements"
  | "customer_view_only"
  | "canExportNewProducts"
  | "preferences"
  | "canViewCustomerLists"
  | "canMigrateSalesRepInvoiceItems"
  | "canEditOrderRequests"
  | "canShuntOrderRequests"
  | "canAssignCustomerTags"
  | "canDeleteUnexportedProducts"
  | "canViewInternalTickets"
  | "canBypassSpendLimitOnNewBusiness"
  | "isHrAdmin"
  | "hrAreaOnly"
  | "customerAreaQuotesOnly"
  | "managedByHrUsers"
  | "canManageProductMasterTags"
  | "canAssignProductMasterTags"
  | "canManageSalesRestrictions"
  | "hrManagedUsers"
  | "canManageBuyingGroups"
  | "canApproveByPassMinMargins"
  | "canApproveElevatedMinPriceBypass"
  | "canManageNamedPriceBands"
  | "can_set_site_banner_message"
  | "canManageSharedPriceBands"
  | "can_approve_manual_invoice_items"
  | "can_create_manual_invoice_items"
  | "canDeleteUniversalPriceBands"
  | "canManageRoles"
  | "access_url"
> & {
  microsoft_user_details: Record<string, any> | null
  defaultSupportTicketGroup: IRecord<"support_ticket_groups", "name" | "isTechnical"> | null
  defaultCustomerTagFilter:
    | (IRecord<"tag_values", "value"> & {
        tag?: IRecord<"tags", "key">
      })
    | null
  microsoft_oauth: any
  branch: IRecord<
    "branches",
    "name" | "currency_name" | "consignmentWarehouse" | "euro_pound_exchange_rate"
  >
  customer?: IRecord<"customers", "name" | "canViewPricing" | "fileboyId">
  sub_customers: IRecord<"sub_customers", "name">[]
  accessibleBranches: IRecord<"branches", "name">[]
  hrAccessibleBranches: IRecord<"branches", "name">[]
  defaultWarehouse?: IRecord<"warehouses", "name">
  salesRepId: string | null
  accessible_reports: Partial<IAccessibleReports>
  country: "all" | "uk" | "ireland"
  role?: IRecord<"roles", "name">
}

export interface IGlobalState {
  loading: boolean
  currentUser: typeof emptyUser | ICurrentUser
  fetchCurrentUser: () => Promise<void>
  signedIn: boolean
  signOut: () => Promise<void>
  title: string
  setTitle: (val: string) => void
  breadcrumb: any[]
  setBreadcrumb: (newCrumbs: any[]) => void
  setCurrentUser: (newUser: ICurrentUser) => void
  currency: "£" | "€" | "$" | ""
}

export interface IEnsureGlobalState {
  loading: boolean
  currentUser: ICurrentUser
  fetchCurrentUser: () => Promise<void>
  signedIn: boolean
  signOut: () => Promise<void>
  title: string
  setTitle: (val: string) => void
  breadcrumb: any[]
  setBreadcrumb: (newCrumbs: any[]) => void
  setCurrentUser: (newUser: ICurrentUser) => void
  currency: "£" | "€" | "$" | ""
}

export const GlobalStateContext = React.createContext<IGlobalState | null>(null)

interface IProps {
  children: React.ReactNode
}

export { EOS_SKEEM_TOKEN }

export function GlobalStateProvider({ children }: IProps) {
  const skeem = window.skeem
  const [currentUser, setCurrentUser] = useState<typeof emptyUser | ICurrentUser>(emptyUser)
  const [title, setTitle] = useState("")
  const [breadcrumb, setBreadcrumb] = useState([] as IBreadCrumb[])
  const [loading, setLoading] = useState(true)

  async function initSession() {
    const skeemToken = getLocalStorageWithExpiry(EOS_SKEEM_TOKEN)
    if (skeemToken) {
      skeem.setAuthToken(skeemToken)
      try {
        await skeem.me()
        await fetchCurrentUser()
      } catch (e) {
        console.error(e)
        clearStoredSession()
        // The retry runs anonymously, so it should do nothing. If the client
        // still thinks it has a session it throws again, and letting that reject
        // would surface a stale token as a 500 page — boot signed out instead.
        try {
          await fetchCurrentUser()
        } catch (retryError) {
          if (!isExpiredSessionError(retryError)) throw retryError
          console.error("Signed out: the stored session no longer resolves", retryError)
        }
      }
    } else {
      // clear the auth token if it exists to prevent stale authed user despite UI showing logged out
      skeem.setAuthToken(null)
    }
  }

  useEffect(() => {
    initSession().finally(() => setLoading(false))
  }, [])

  async function fetchCurrentUser() {
    if (skeem.currentSession.id) {
      const response = await skeem.fetchMe<ICurrentUser>([
        "name",
        "first_name",
        "last_name",
        "email",
        "isAdmin",
        "isSuperAdmin",
        "isAccounts",
        "canExportPurchaseOrdersToSage",
        "canDespatchOrdersInSage",
        "canEditGlobalUserPermissions",
        "can_manage_sage_auth",
        "can_manage_customer_slide_show",
        "userAccountType",
        "last_seen",
        "canManageProducts", // product masters etc
        "canManageConsignmentProducts",
        "canRunSageImports",
        "canViewCRM",
        "canViewBranchReports",
        "isBlocked",
        "canAccessSystemSettings",
        "beta_features",
        "accessible_reports",
        "isHrAdmin",
        "hrAreaOnly",
        "canEditBranchSettings",
        "canUseNonPriceBandPricesOnQuotesOrders",
        "canSetPricesBelowMinimumMargins",
        "microsoft_oauth",
        "canEditCustomersPricing",
        "canDispatchCashSalesOrderWithNoCreditLimit",
        "canViewGlobalMasterPriceImports",
        "canManageBetaFeatureAccess",
        "canViewPreviewFeatures",
        "canUpdatePurchaseOrdersInSage",
        "canManageSupportTicketSettings",
        "canCreateSageProducts",
        "canEditExistingProducts",
        "dashboard_type",
        "canManageStockTakes",
        "canManageSalesRepTargets",
        "canManageBranchTargets",
        "hasExistingTradeAccount",
        "companyName",
        "canDispatchOverCreditLimitOrders",
        "canGrantReportPermissions",
        "canManageSalesRepAssignments",
        "canDeletePriceBands",
        "canCreateStockTransfers",
        "reports_viewable_branches",
        "canManageUniversalPricing",
        "canUpdateManagingUsersOnSharePriceBands",
        "canUpdateManagingUsersOnPriceBands",
        "canCopyPriceBandsInterBranch",
        "canSendBulkEmails",
        "canViewOtherUsersEvents",
        "customerIsPrimaryAccountHolder",
        "customerCanViewFinances",
        "canViewFortusLiveAccounts",
        "canGuestLoginToCustomerAccounts",
        "canManageCustomerTags",
        "canManageHolidayRequests",
        "canArchiveQuotes",
        "canArchiveOrders",
        "canExportCustomers",
        "canManageCRMPermissions",
        "canManageHRPermissions",
        "preferences",
        "enablePhoneMFA",
        "canViewCustomerStatements",
        "customer_view_only",
        "canMigrateSalesRepInvoiceItems",
        "canViewCustomerLists",
        "canEditOrderRequests",
        "canShuntOrderRequests",
        "canExportNewProducts",
        "canAssignCustomerTags",
        "canDeleteUnexportedProducts",
        "canViewInternalTickets",
        "canBypassSpendLimitOnNewBusiness",
        "customerAreaQuotesOnly",
        "hrManagedUsers",
        "managedByHrUsers",
        "canManageProductMasterTags",
        "canAssignProductMasterTags",
        "canManageSalesRestrictions",
        "canManageBuyingGroups",
        "canApproveByPassMinMargins",
        "canApproveElevatedMinPriceBypass",
        "canManageNamedPriceBands",
        "can_set_site_banner_message",
        "canEditLinkTooltips",
        "canManageSharedPriceBands",
        "microsoft_user_details",
        "can_approve_manual_invoice_items",
        "can_create_manual_invoice_items",
        "canDeleteUniversalPriceBands",
        "canManageRoles",
        "access_url",
        {
          name: "defaultCustomerTagFilter",
          attributes: ["value", { name: "tag", attributes: ["key"] }],
        },
        { name: "defaultSupportTicketGroup", attributes: ["name", "isTechnical"] },
        { as: "salesRepId", value: { path: ["sales_rep", "id"] } },
        { name: "defaultWarehouse", attributes: ["name"] },
        { name: "accessibleBranches", attributes: ["name"] },
        { name: "accessibleBulkEmailBranches", attributes: ["name"] },
        { name: "hrAccessibleBranches", attributes: ["name"] },
        { name: "customer", attributes: ["name", "canViewPricing", "fileboyId"] },
        {
          as: "sub_customers",
          value: { path: ["contact", { name: "sub_customers", attributes: ["name"] }] },
        },
        {
          name: "branch",
          attributes: [
            "name",
            "currency_name",
            "consignmentWarehouse",
            "euro_pound_exchange_rate",
          ],
        },
        { name: "role", attributes: ["name"] },
      ])

      if (response.role) {
        const role = await window.skeem.fetch2<IRecord<"roles", IRoleSetting>>({
          first: { name: "roles", matches: { id: response.role.id }, attributes: roleSettings },
        })
        replaceRoleSettings(response as any, role)
      }

      if (response.isBlocked) {
        skeem.logout({ allSessions: true })
        return
      }

      response.country = response.branch?.id
        ? response.branch.id === IRELAND_BRANCH_ID
          ? "ireland"
          : "uk"
        : "all"

      const token = await skeem.getAuthToken()
      setLocalStorageWithExpiry(EOS_SKEEM_TOKEN, token ?? "", 1000 * 60 * 60 * 10)
      watchForTokenExpiry()
      setCurrentUser(response)
      // A session that resolves ends the incident, so the next expiry is free to
      // redirect rather than being treated as part of a loop.
      clearExpiredSessionRedirectState()

      syncAccessUrl(response).catch(console.error)
      return response
    }
  }

  const watchIntervalRef = useRef<any>(null)
  function watchForTokenExpiry() {
    if (watchIntervalRef.current) {
      return
    }

    watchIntervalRef.current = setInterval(() => {
      const skeemToken = getLocalStorageWithExpiry(EOS_SKEEM_TOKEN)
      if (!skeemToken) {
        signOut()
        window.location.href = "/"
      }
    }, 30000)
  }

  async function signOut() {
    await skeem
      .logout()
      .then(async () => {
        console.log("reloading...")
        localStorage.removeItem(EOS_SKEEM_TOKEN)
        // Drops any branch guest session, and with it the staff token it was holding
        clearBranchGuestSession()
        // Wait to allow any logout processes to complete and requests to finish
        await new Promise(resolve => setTimeout(resolve, 500))
        window.location.href = "/"
      })
      .catch((e: unknown) => {
        // silence logoutNotAuthenticated errors and clear local session
        if (e instanceof Error && e.message == "logoutNotAuthenticated") {
          console.error("Error during sign out - cleared local session", e)
          localStorage.removeItem(EOS_SKEEM_TOKEN)
          clearBranchGuestSession()
          window.location.href = "/"
        } else {
          throw e
        }
      })
  }

  function getCurrency() {
    if (!currentUser || !currentUser.branch) {
      return ""
    }

    return currencyNameToSymbol(currentUser.branch.currency_name)
  }

  async function syncAccessUrl(user: { id: string; access_url?: string }) {
    // get unique base urls
    const basePath = window.location.hostname
    const accessUrls = user.access_url?.split("|") ?? []
    // if user has none assigned or does not have the current basepath in the assigned list
    // update them with the assigned based paths
    if (!user.access_url || !user.access_url?.includes(basePath)) {
      accessUrls.push(basePath)
      await skeem.mutate({
        skeem_users: { update: { id: user.id, access_url: accessUrls.join("|") } },
      })
      return
    }
  }

  const value: IGlobalState = {
    loading,
    currentUser,
    fetchCurrentUser: async () => {
      await fetchCurrentUser()
    },
    signedIn: !!currentUser.id,
    signOut,
    title,
    setTitle,
    breadcrumb,
    setBreadcrumb,
    setCurrentUser,
    currency: getCurrency(),
  }

  return <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>
}

function replaceRoleSettings(
  user: ICurrentUser & { role: IRecord<"roles", "name"> },
  role: IRecord<"roles", IRoleSetting>,
) {
  console.log("[ROLE] Assigning role settings to user for role", user.role.name)
  roleSettings.forEach(settingKey => {
    user[settingKey] = role[settingKey]
  })

  console.log("[ROLE] ASSIGNED SETTINGS", role)
}

export function setLocalStorageWithExpiry(key: string, value: string, ttl: number) {
  const now = new Date()

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  }
  localStorage.setItem(key, JSON.stringify(item))
}

function getLocalStorageWithExpiry(key: string) {
  const item = localStorage.getItem(key)
  if (!item) {
    return null
  }

  const parsedItem = JSON.parse(item)
  const now = new Date()

  // If the item is expired, remove it from localStorage
  // and return null
  if (parsedItem.expiry < now.getTime()) {
    console.log("Expired")
    localStorage.removeItem(key)
    return null
  }

  return parsedItem.value
}
